/**
 * "From your calendar" — recent meetings offered back as one-click entries.
 *
 * The tracker's card, on both Time › Today and the phone's Today. Each row
 * carries the project guessed from the title as a dropdown, so a wrong guess
 * is fixed in place rather than after logging. Logging uses the event's own
 * start and end, so the entry lands on the day and clock it happened.
 *
 * Not connected: a single-line hint with Connect (to Settings › Calendars) and
 * Dismiss, which is the tracker's empty state too — no sample events.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { eyebrow } from "@/components/atoms";
import { useToast } from "@/context/ToastContext";
import { effectiveRate, useClients, useTimeMutations } from "@/hooks/useTime";
import { defaultKind, kindFields } from "@/lib/billing";
import { useCalendarHint, useCalendarSuggestions, type CalendarEvent } from "@/lib/calendarImport";
import { durationLabel, toIsoLocal } from "@/lib/timeRollup";
import type { Project, TimeEntry } from "@/lib/types";

export function CalendarCard({ entries, projects, compact = false }: {
  entries: TimeEntry[];
  projects: Project[];
  /** Phone layout: rows stack, targets are taller. */
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const logMeeting = useLogMeeting(projects);
  const cal = useCalendarSuggestions(entries, projects);
  const [hintHidden, hideHint] = useCalendarHint();
  /** Per-row project override, keyed by event id. */
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const selectable = projects.filter((p) => p.status !== "archived" && p.kind !== "inbox");
  const connect = () => navigate("/settings#calendars");

  if (!cal.connected) {
    if (hintHidden) return null;
    return (
      <div style={{ ...emptyBox, marginTop: compact ? 14 : 0, padding: compact ? 12 : 14 }}>
        {!compact && <span style={eyebrow}>Calendar</span>}
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "var(--muted)" }}>
          {compact
            ? "Calendar not connected — meetings would show here as one-tap entries."
            : "Not connected — once a calendar is linked, your meetings show up here as one-click entries."}
        </span>
        <button onClick={connect} style={{ ...ghostBtn, flex: "none", minHeight: compact ? 36 : undefined }}>Connect</button>
        <button onClick={hideHint} style={linkBtn}>Dismiss</button>
      </div>
    );
  }

  // A feed that failed says so, quietly — but only when there is nothing
  // useful to show instead.
  const problem = cal.failure ?? (cal.errors.length ? `${cal.errors[0].label}: ${cal.errors[0].message}` : null);
  if (cal.suggestions.length === 0) {
    if (!problem) return null;
    return (
      <div style={{ ...emptyBox, marginTop: compact ? 14 : 0, padding: compact ? 12 : 14 }}>
        {!compact && <span style={eyebrow}>Calendar</span>}
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "var(--muted)" }}>
          Couldn’t read your calendar — {problem}
        </span>
        <button onClick={connect} style={{ ...ghostBtn, flex: "none" }}>Settings</button>
      </div>
    );
  }

  async function logIt(ev: CalendarEvent) {
    setBusy(ev.id);
    if (await logMeeting(ev, picked[ev.id] ?? ev.projectId)) cal.dismiss(ev.id);
    setBusy(null);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: compact ? 14 : 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={eyebrow}>From your calendar</span>
        <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
        <button onClick={cal.dismissAll} style={linkBtn}>Dismiss all</button>
      </div>

      {cal.suggestions.map((ev) => {
        const projectId = picked[ev.id] ?? ev.projectId;
        const color = projects.find((p) => p.id === projectId)?.color ?? "var(--muted3)";
        const day = dayTag(ev.date);
        return (
          <div key={ev.id} style={{
            display: "flex", alignItems: compact ? "stretch" : "center", gap: compact ? 8 : 12,
            flexDirection: compact ? "column" : "row",
            padding: compact ? 12 : "11px 14px", borderRadius: 10,
            border: "1px dashed var(--accentLine)", background: "var(--surface2)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
              <span className="mono" style={{ flex: "none", width: 46, fontSize: 12.5, color: "var(--ink2)" }}>
                {durationLabel(ev.durationMinutes)}
              </span>
              <span style={{ flex: "none", width: 8, height: 8, borderRadius: 2, background: color }} />
              <span title={ev.summary} style={{
                flex: 1, minWidth: 0, fontSize: 14.5, color: "var(--ink)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{ev.summary}</span>
              {day && <span style={dayChip}>{day}</span>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
              <select
                value={projectId}
                onChange={(e) => setPicked((m) => ({ ...m, [ev.id]: e.target.value }))}
                aria-label={`Project for ${ev.summary}`}
                style={{ ...selectStyle, flex: compact ? 1 : "none", minHeight: compact ? 36 : undefined }}
              >
                <option value="">No project</option>
                {selectable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button onClick={() => cal.dismiss(ev.id)} style={linkBtn}>Dismiss</button>
              <button
                onClick={() => void logIt(ev)}
                disabled={busy === ev.id}
                style={{ ...logBtn, minHeight: compact ? 36 : undefined, opacity: busy === ev.id ? 0.6 : 1 }}
              >Log it</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Log a meeting as an entry: its own start and end, the meeting title as the
 * description, and the rate and kind of the project it goes to. Shared by this
 * card and the Today page's "Not logged" list. Resolves false if it failed,
 * after saying so.
 */
export function useLogMeeting(projects: Project[]) {
  const { data: clients = [] } = useClients();
  const { logEntry } = useTimeMutations();
  const toast = useToast();
  return async (ev: CalendarEvent, projectId: string): Promise<boolean> => {
    const project = projects.find((p) => p.id === projectId);
    const r = effectiveRate(project, clients);
    const k = kindFields(defaultKind(project, !!r.clientId), r.rate);
    try {
      await logEntry.mutateAsync({
        projectId: projectId || undefined,
        start: ev.start, end: ev.end,
        durationSeconds: ev.durationMinutes * 60,
        note: ev.summary,
        billable: k.billable, internal: k.internal,
        source: "manual",
        rate: k.rate, currency: r.currency, clientId: r.clientId,
      });
      toast(`Logged ${durationLabel(ev.durationMinutes)} · ${ev.summary}`);
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : "Could not save that entry");
      return false;
    }
  };
}

/** "Yesterday" / "Mon" for past days; nothing for today, which is the default. */
function dayTag(date: string): string | null {
  const today = new Date();
  if (date === toIsoLocal(today)) return null;
  const y = new Date(today); y.setDate(y.getDate() - 1);
  if (date === toIsoLocal(y)) return "Yesterday";
  const [yy, mm, dd] = date.split("-").map(Number);
  return new Date(yy, mm - 1, dd).toLocaleDateString(undefined, { weekday: "short" });
}

const emptyBox: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
  borderRadius: 10, border: "1px dashed var(--line2)", background: "var(--surface2)",
};
const ghostBtn: React.CSSProperties = {
  padding: "8px 14px", borderRadius: 8, fontSize: 13.5, fontWeight: 500, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
const linkBtn: React.CSSProperties = {
  flex: "none", border: "none", background: "none", padding: "4px 6px",
  fontSize: 12.5, color: "var(--muted)", cursor: "pointer",
};
const logBtn: React.CSSProperties = {
  flex: "none", padding: "7px 13px", borderRadius: 8, cursor: "pointer",
  fontSize: 12.5, fontWeight: 600,
  border: "1px solid var(--accentLine)", background: "var(--accentTint)", color: "var(--accent)",
};
const selectStyle: React.CSSProperties = {
  maxWidth: 200, padding: "6px 9px", borderRadius: 8, fontSize: 12.5, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--bg)", color: "var(--ink2)",
};
const dayChip: React.CSSProperties = {
  flex: "none", fontSize: 11, padding: "2px 7px", borderRadius: 5,
  border: "1px solid var(--line)", background: "var(--surface)", color: "var(--muted)",
};
